import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Search, Download } from 'lucide-react';
import { fetchPastRetiredEmployeesReport } from '../../services/reportsApi';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/dateUtils';

const PastRetiredEmployeesReportView = () => {
  const { hasPermission } = useAuth();
  const currentYrNum = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(String(currentYrNum));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const abortControllerRef = useRef(null);

  const loadData = useCallback(async (yearToFetch) => {
    const yr = String(yearToFetch || selectedYear || '').trim();
    if (!yr || !/^\d{4}$/.test(yr)) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError('');
    
    try {
      const response = await fetchPastRetiredEmployeesReport(yr, abortControllerRef.current.signal);
      const items = response.items || [];
      // Sort ascending by retirement date so the first retired employee in that year appears first
      items.sort((a, b) => {
        if (!a.retirement_date) return 1;
        if (!b.retirement_date) return -1;
        const dateDiff = new Date(a.retirement_date) - new Date(b.retirement_date);
        if (dateDiff !== 0) return dateDiff;
        return (a.emp_code || '').localeCompare(b.emp_code || '');
      });
      setData(items);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch retired employees report');
      }
    } finally {
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedYear]);

  useEffect(() => {
    const trimmed = String(selectedYear || '').trim();
    if (/^\d{4}$/.test(trimmed)) {
      const timer = setTimeout(() => {
        loadData(trimmed);
      }, 350);
      return () => clearTimeout(timer);
    } else {
      setData([]);
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [selectedYear, loadData]);

  const handleYearChange = (e) => {
    const val = e.target.value;
    // Allow digits only up to 4 digits
    const cleaned = val.replace(/\D/g, '').slice(0, 4);
    setSelectedYear(cleaned);
  };

  const handleYearKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = String(selectedYear || '').trim();
      if (/^\d{4}$/.test(trimmed)) {
        loadData(trimmed);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const yr = parseInt(selectedYear, 10);
      if (!isNaN(yr)) {
        setSelectedYear(String(yr + 1));
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const yr = parseInt(selectedYear, 10);
      if (!isNaN(yr)) {
        setSelectedYear(String(yr - 1));
      }
    }
  };

  const filteredData = data.filter(emp => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      (emp.emp_code && emp.emp_code.toLowerCase().includes(lowerQuery)) ||
      (emp.full_name && emp.full_name.toLowerCase().includes(lowerQuery)) ||
      (emp.department && emp.department.toLowerCase().includes(lowerQuery)) ||
      (emp.designation && emp.designation.toLowerCase().includes(lowerQuery))
    );
  });

  const downloadExcel = () => {
    if (filteredData.length === 0) return;

    const exportData = filteredData.map(item => ({
      'Employee Code': item.emp_code,
      'Employee Name': item.full_name,
      'Department': item.department || '-',
      'Designation': item.designation || '-',
      'Retirement Date': formatDate(item.retirement_date, '-'),
      'Status': item.status,
      'Retirement Category': item.retirement_type || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Retired Employees');
    XLSX.writeFile(workbook, `Retired_Employees_${selectedYear}.xlsx`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters Area */}
      <div className="bg-white p-4 border-b border-gray-200">
        <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex space-x-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  Retirement Year
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="YYYY"
                  value={selectedYear}
                  onChange={handleYearChange}
                  onKeyDown={handleYearKeyDown}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white w-32"
                  title="Enter 4-digit retirement year (Use Up/Down arrows to step year)"
                />
              </div>
            </div>
          
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm w-48 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            {hasPermission('report:export') && (
<button
              onClick={downloadExcel}
              disabled={filteredData.length === 0}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
)}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retirement Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retirement Category</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Loading data...</p>
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((emp, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{emp.emp_code}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{emp.full_name}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.department || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.designation || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(emp.retirement_date, '-')}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {emp.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{emp.retirement_type || '-'}</td>
                    </tr>
                  ))
                ) : !/^\d{4}$/.test(String(selectedYear || '').trim()) ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      Please enter a valid 4-digit retirement year (e.g. {currentYrNum}).
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No retired employees found for year {selectedYear}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastRetiredEmployeesReportView;
